import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navbar(): JSX.Element {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isZonesOpen, setIsZonesOpen] = useState(false);
  const navigate = useNavigate();

  // Guard against SSR where `window` is undefined
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Initialise state in case the page is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (id: string) => {
    if (!id) return;
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.warn(`Element with id "${id}" not found.`);
    }
  };

  return (
    <nav
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${isScrolled ? 'bg-white shadow-lg py-3' : 'bg-transparent py-4'}
      `}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => scrollToSection('hero')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md">
              <i className="ri-truck-fill text-base text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span
                className={`
                  text-lg font-extrabold tracking-tight transition-colors
                  ${isScrolled ? 'text-gray-900' : 'text-white'}
                `}
              >
                Transporteur
                <span className={isScrolled ? 'text-red-600' : 'text-red-400'}>
                  {' '}express
                </span>
              </span>
              <span
                className={`
                  text-[9px] font-medium tracking-widest uppercase
                  ${isScrolled ? 'text-gray-400' : 'text-white/60'}
                `}
              >
                Île-de-France
              </span>
            </div>
          </div>

          {/* Navigation Desktop + CTA */}
          <div className="flex items-center space-x-2">
            {/* Navigation Links - Hidden on mobile, visible on desktop */}
            <div className="hidden lg:flex items-center space-x-1">
              <button
                onClick={() => scrollToSection('services')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                Tarifs
              </button>
              
              {/* Zones Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsZonesOpen(true)}
                onMouseLeave={() => setIsZonesOpen(false)}
              >
                <button
                  onClick={() => scrollToSection('zones')}
                  className={`
                    px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1
                    ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                  `}
                >
                  Zones
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${isZonesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {/* Dropdown Menu */}
                {isZonesOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-64 rounded-lg shadow-xl py-2 ${isScrolled ? 'bg-white' : 'bg-gray-900'}`}>
                    <button
                      onClick={() => navigate('/transporteur-express-nanterre')}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                    >
                      <i className="ri-map-pin-line mr-2 text-red-500"></i>
                      Nanterre (92)
                    </button>
                    <button
                      onClick={() => navigate('/transporteur-palettes-rungis')}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                    >
                      <i className="ri-map-pin-line mr-2 text-red-500"></i>
                      Rungis (94)
                    </button>
                    <button
                      onClick={() => navigate('/livraison-urgente-evenementiel-villepinte')}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                    >
                      <i className="ri-map-pin-line mr-2 text-red-500"></i>
                      Villepinte (93)
                    </button>
                    <div className={`my-2 border-t ${isScrolled ? 'border-gray-200' : 'border-white/10'}`}></div>
                    <button
                      onClick={() => navigate('/transport-paris')}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                    >
                      <i className="ri-map-pin-line mr-2 text-red-500"></i>
                      Paris (75)
                    </button>
                    <button
                      onClick={() => navigate('/coursier-paris')}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                    >
                      <i className="ri-map-pin-line mr-2 text-red-500"></i>
                      Coursier Paris
                    </button>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => navigate('/avis-google')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                Avis Google
              </button>
              <button
                onClick={() => navigate('/a-propos')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                À propos
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                FAQ
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                Contact
              </button>
              <button
                onClick={() => navigate('/blog')}
                className={`
                  px-4 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer
                  ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white/90 hover:bg-white/10'}
                `}
              >
                Blog
              </button>
            </div>

            {/* CTA Button */}
            <a
              href="tel:+33632410246"
              className="bg-red-600 text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <i className="ri-phone-fill text-sm" />
              Commander
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}